import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Field } from 'redux-form';

import Box from '@material-ui/core/Box';

import VariantInputGroupField from 'bento-ordering/menus/components/VariantInputGroupField';
import { getVariantPrice } from 'bento-ordering/menus/utils/cartModifications';

const RADIO_BUTTON = 'radiobutton';
const CHECKBOX = 'checkbox';

class VariantGroupForm extends React.Component {
  static propTypes = {
    form: PropTypes.shape({
      menuItemDetail: PropTypes.shape({ values: PropTypes.shape({}) })
    }),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            slug: PropTypes.string,
            id: PropTypes.number,
            max_qty: PropTypes.number,
            min_qty: PropTypes.number,
            helper_text: PropTypes.string,
            options: PropTypes.arrayOf(
              PropTypes.shape({
                name: PropTypes.string,
                slug: PropTypes.string,
                price: PropTypes.number
              })
            )
          })
        )
      })
    )
  };

  getVariantName = (name, minQty, maxQty) => {
    if (minQty === maxQty) {
      return `${name} (Choose ${minQty})`;
    } else if (minQty > 1 && !maxQty) {
      return `${name} (Choose at least ${minQty})`;
    } else if (minQty === 0 && maxQty > 0) {
      return `${name} (Choose up to ${maxQty})`;
    } else if (minQty > 0 && maxQty > 0) {
      return `${name} (Choose between ${minQty}-${maxQty})`;
    }
  };

  /**
   * Determine whether this should be a dropdown
   * or checkbox base on the minQty and maxQty property
   * maxQty > 1 || (minQty === 0 && maxQty > 1) -> checkbox
   * minQty > 0 && maxQty === 1 -> radiobutton
   */
  getFieldType = (minQty, maxQty) => {
    minQty = parseInt(minQty);
    maxQty = parseInt(maxQty);

    if (minQty > 0 && maxQty === 1) {
      return RADIO_BUTTON;
    }
    // see https://docs.google.com/document/d/1T0_kHuZRaiXkJbWeVvvduwWRZfa7Tmrv2DQMS6UzIxk/edit#
    // max_qty > 1) || (Min_qty === 0, max_qty  => 1
    return CHECKBOX;
  };

  /*
   * Parse the variant data to fit our radioButtonGroup and
   * checkbox group component
   */
  getParsedVariantOptions = options => {
    return options.reduce((acc, currentValue) => {
      const price = getVariantPrice(currentValue.price_variance);

      acc.push({
        label: currentValue.name,
        value: currentValue.slug,
        price
      });
      return acc;
    }, []);
  };

  /**
   * Track if the number we selected the max selections
   * for a given checkbox. This function counts the number
   * of selected values for the specific checkbox variant
   * in redux form.
   */
  isMaxVariantOptionsSelected = (maxQuantity, slug) => {
    const { form } = this.props;
    const values = get(form.menuItemDetail, 'values', {});

    if (!values || Object.values(values).length === 0 || !values[slug]) {
      return false;
    }

    const selected = Object.values(values[slug]);
    if (!selected) {
      return false;
    }
    const trueValues = selected.filter(option => option).length;
    return trueValues >= maxQuantity;
  };

  /**
   * Render radio inputs or checkbox inputs
   * base on the variant data returned. We are following a similar
   * structure to the catering store. There will only be one variants
   * object. The fields array under variants represent each variant
   * a user can have for their item. The options array under a field
   * are the modifiers for that particular variant.
   */
  renderVariantGroups = () => {
    const fields = get(this.props.variants, [0, 'fields']);
    const values = get(this.props.form.menuItemDetail, 'values', {});

    if (fields) {
      return fields.map(field => {
        const {
          name,
          slug,
          id,
          max_qty,
          min_qty,
          options,
          helper_text
        } = field;
        const optionsList = this.getParsedVariantOptions(options);
        const maxQty = max_qty || optionsList.length;
        const variantName = this.getVariantName(
          name,
          parseInt(min_qty),
          parseInt(maxQty)
        );
        const numOfSelection = values[slug]
          ? Object.values(values[slug]).filter(v => v).length
          : 0;

        return (
          <Box
            key={id}
            borderBottom="1px solid"
            color="borders.primary"
            data-testid={`variant-field-${id}`}
          >
            <Box pt={2.5} pb={2}>
              <Field
                key={id}
                title={variantName}
                name={slug}
                component={VariantInputGroupField}
                helperText={helper_text}
                fieldType={this.getFieldType(min_qty, maxQty)}
                disabled={
                  min_qty === 1 && max_qty === 1
                    ? false
                    : this.isMaxVariantOptionsSelected(maxQty, slug)
                }
                list={optionsList}
                checked={false}
                required={min_qty > 0}
                selectionSatisfied={numOfSelection >= min_qty}
              />
            </Box>
          </Box>
        );
      });
    }

    return null;
  };

  render() {
    return this.renderVariantGroups();
  }
}

export default VariantGroupForm;
